/**
 * Raccourcis du socle d'éditeur (ADR-146 §5.3, gestes repris de `migrationweb`) : une table pure, testée,
 * de la touche à la commande. Ils ne valent que dans l'éditeur (jamais un raccourci global) et ne prennent
 * pas la main pendant une saisie, sauf annuler et rétablir hors d'un champ texte.
 */

export type EditorCommand = 'undo' | 'redo' | 'duplicate' | 'remove' | 'search' | 'palette' | 'escape' | 'parent' | 'move_up' | 'move_down' | 'zoom_in' | 'zoom_out' | 'zoom_fit' | 'save';

export interface KeyLike {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

/** Commande d'une touche, sans regarder la cible. */
export function shortcutOf(event: KeyLike): EditorCommand | null {
  const mod = event.ctrlKey === true || event.metaKey === true;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  if (mod && key === 'z') return event.shiftKey === true ? 'redo' : 'undo';
  if (mod && key === 'y') return 'redo';
  if (mod && key === 'd') return 'duplicate';
  if (mod && key === 'k') return 'palette';
  if (mod && key === 's') return 'save';
  // Zoom de l'aperçu (Figma, Framer) : Ctrl + et Ctrl − ; Ctrl 0 revient à « Ajuster ».
  if (mod && (key === '+' || key === '=')) return 'zoom_in';
  if (mod && (key === '-' || key === '_')) return 'zoom_out';
  if (mod && key === '0') return 'zoom_fit';
  if (mod && key === 'ArrowUp') return 'parent';
  if (event.altKey === true && key === 'ArrowUp') return 'move_up';
  if (event.altKey === true && key === 'ArrowDown') return 'move_down';
  if (!mod && event.altKey !== true && (key === 'Delete' || key === 'Backspace')) return 'remove';
  if (!mod && key === '/') return 'search';
  if (key === 'Escape') return 'escape';
  return null;
}

/** Une cible où l'on tape du texte : les raccourcis sans modificateur ne s'y appliquent pas. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || typeof (target as { tagName?: unknown }).tagName !== 'string') return false;
  const element = target as HTMLElement;
  const tag = element.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select' || element.isContentEditable) return true;
  if (tag !== 'input') return false;
  const type = (element as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'color'].includes(type);
}

/**
 * Commande à exécuter pour une touche dans l'éditeur : pendant une saisie, seuls Échap et la palette
 * (Ctrl K) restent ; annuler et rétablir appartiennent au champ (son propre historique).
 */
export function editorShortcut(event: KeyLike, target: EventTarget | null): EditorCommand | null {
  const command = shortcutOf(event);
  if (command === null) return null;
  if (!isTypingTarget(target)) return command;
  return command === 'escape' || command === 'palette' || command === 'save' || command === 'zoom_in' || command === 'zoom_out' || command === 'zoom_fit' ? command : null;
}

/** Aide affichée au pied du canevas (touche, effet). */
export const SHORTCUT_HELP: ReadonlyArray<{ keys: string; text: string }> = [
  { keys: 'Clic', text: 'sélectionner' },
  { keys: '/', text: 'chercher un bloc' },
  { keys: 'Ctrl K', text: 'commandes' },
  { keys: 'Ctrl D', text: 'dupliquer' },
  { keys: 'Suppr', text: 'supprimer' },
  { keys: 'Ctrl ↑', text: 'parent' },
  { keys: 'Alt ↑↓', text: 'monter / descendre' },
  { keys: 'Ctrl Z', text: 'annuler' },
  { keys: 'Ctrl Maj Z', text: 'rétablir' },
  { keys: 'Ctrl S', text: 'enregistrer' },
  { keys: 'Ctrl + / Ctrl −', text: 'zoom' },
  { keys: 'Ctrl 0', text: 'ajuster' },
];
